import flatten from 'lodash/flatten';
import forEach from 'lodash/forEach';
import invokeMap from 'lodash/invokeMap';
import map from 'lodash/map';
import reduce from 'lodash/reduce';
import without from 'lodash/without';
import SVG from 'svg.js';
import eventbus from 'tiny-emitter/instance';

import { FULLSEATGAP, RENDERER_MODE, TYPE } from '@/constants';
import Seatmap from '../seatmap';
import Model, { ModelParams } from './model';
import { Row } from './row';

import Matrix from '@/core/Matrix';
import Point from '@/core/Point';
import { SEATRADIUS } from '@/constants';
import { Seat } from './seat';

export class Block extends Model {
  rows: Row[] = [];
  param_rows: Row[] = [];
  visible: boolean = false;

  declare label: SVG.Text | null;

  constructor(params: ModelParams) {
    super(params);
    this.initView();
    this.render();

    if (RENDERER_MODE) {
      this.setupEventListeners();
    }
  }

  get type() {
    return TYPE.BLOCK;
  }

  get seats() {
    return flatten(map(this.rows, (row) => row.seats));
  }

  get firstSeat() {
    return this.firstRow.firstSeat;
  }

  get firstRow() {
    return this.rows[0];
  }

  get lastRow() {
    return this.rows[this.rows.length - 1];
  }

  get rowsCount() {
    return this.rows.length;
  }

  get seatsCountInFirstRow() {
    return this.firstRow.seatsCount;
  }

  show() {
    this.canvas.add(this.view);
    this.render();
    invokeMap(this.rows, 'updateDynamicElements');
  }

  hide() {
    this.view.remove();
  }

  setupEventListeners() {
    eventbus.on('camera:zoomed', this.update, this);
    eventbus.on('camera:panned', this.update, this);
  }

  removeEventListeners() {
    eventbus.off('camera:zoomed', this.update);
    eventbus.off('camera:panned', this.update);
  }

  rbox() {
    return reduce(this.seats, (acc, seat) => acc.merge(seat.rbox()) as SVG.RBox, this.firstSeat.rbox());
  }

  select() {
    forEach(this.seats, (seat) => seat.select());
  }

  deselect() {
    forEach(this.seats, (seat) => seat.deselect());
  }

  addRow(row: Row) {
    this.view.add(row.view);
    this.rows.push(row);
  }

  removeRow(row: Row) {
    this.rows = without(this.rows, row);
    // TODO: This was commented out for a reason
    // and was keeping empty blocks in the json causing section scaling to break
    if (this.rows.length === 0) {
      this.destroy(false);
    }
  }

  removeRows(count: number) {
    const seatsToDelete = [];
    for (let i = 0; i < count; i++) {
      seatsToDelete.push(...this.lastRow.seats);
      this.lastRow.destroy(true, true);
    }
    this.app.seatmap.removeUnits(seatsToDelete);
    return seatsToDelete;
  }

  initializeAndAddRows(count: number) {
    const newSeats = [];
    for (let i = 0; i < count; i++) {
      const lastRow = this.lastRow;
      const seats = lastRow.seats;

      const secondToLastRow = this.rows[this.rows.length - 2];
      const firstSeat = lastRow.firstSeat;
      const secondSeat = secondToLastRow?.firstSeat;
      const lastRowSecondSeat = lastRow.seats[1];

      const [x1, y1] = firstSeat.attributes.position;
      let vecDown = [0, -(this.app.seatmap.lastUsedRowSpacing + SEATRADIUS * 2)];

      if (secondSeat) {
        const [x2, y2] = secondSeat.attributes.position;
        vecDown = [x2 - x1, y2 - y1];
      }

      let vecRight = [-(this.app.seatmap.lastUsedSeatSpacing + SEATRADIUS * 2), 0];
      if (lastRowSecondSeat) {
        const [x2, y2] = lastRowSecondSeat.attributes.position;
        vecRight = [x2 - x1, y2 - y1];
      }

      const row = Row.create({ block: this });

      for (let j = 0; j < seats.length; j++) {
        const seat = seats[j];
        const newSeat = new Seat({
          row,
          attributes: {
            ...seat.attributes,
            position: [x1 + vecRight[0] * j - vecDown[0], y1 + vecRight[1] * j - vecDown[1]],
          },
        });
        this.app.seatmap.addUnit(newSeat);
        newSeats.push(newSeat);
      }

      row.renderLabel();
      if (!this.app.seatmap.store.showLabels) {
        row.hideLabel();
      }
    }
    return newSeats;
  }

  initializeAndAddColumns(count: number) {
    const newSeats = [];
    for (let i = 0; i < count; i++) {
      const lastColumn = this.rows.map((row) => row.seats[row.seats.length - 1]);
      const seats = lastColumn;

      const secondToLastColumn = this.rows.map((row) => row.seats[row.seats.length - 2]);
      const firstSeat = lastColumn[0];
      const secondSeat = secondToLastColumn[0];
      const lastColumnSecondSeat = lastColumn[1];

      const [x1, y1] = firstSeat.attributes.position;
      let vecRight = [-(this.app.seatmap.lastUsedSeatSpacing + SEATRADIUS * 2), 0];

      if (secondSeat) {
        const [x2, y2] = secondSeat.attributes.position;
        vecRight = [x2 - x1, y2 - y1];
      }

      let vecDown = [0, -(this.app.seatmap.lastUsedRowSpacing + SEATRADIUS * 2)];
      if (lastColumnSecondSeat) {
        const [x2, y2] = lastColumnSecondSeat.attributes.position;
        vecDown = [x2 - x1, y2 - y1];
      }

      for (let j = 0; j < seats.length; j++) {
        const seat = seats[j];
        const row = this.rows[j];
        const newSeat = new Seat({
          row,
          attributes: {
            ...seat.attributes,
            position: [x1 - vecRight[0] + vecDown[0] * j, y1 - vecRight[1] + vecDown[1] * j],
            label: '?',
          },
        });
        this.app.seatmap.addUnit(newSeat);
        newSeats.push(newSeat);

        row.renderLabel();
      }
    }
    return newSeats;
  }

  removeColumns(count: number) {
    const len = this.rows.length;
    const seatsToDelete = [];

    for (let i = 0; i < len; i++) {
      const row = this.rows[i];
      for (let j = 0; j < count; j++) {
        const seat = row.seats.pop();
        if (seat) {
          seat.destroy();
          seatsToDelete.push(seat);
        }
      }
      row.renderLabel();
    }

    this.app.seatmap.removeUnits(seatsToDelete);
    return seatsToDelete;
  }

  removeLastRow() {
    this.removeRow(this.lastRow);
  }

  calculateLengthsCoeffs() {
    forEach(this.rows, (row) => {
      row.initialSeatsCount = row.seatsCount;
      row.step = 1 / (row.seatsCount - 1);
      row.initialLength = FULLSEATGAP * (row.seatsCount - 1);
      forEach(row.seats, (seat, i) => {
        seat.t = i * row.step;
        seat.pos = i;
      });
    });
  }

  initView() {
    if (this.view) this.destroyView();
    this.view = new SVG.G().addClass('block-shape');
    if (this.attributes.label) {
      this.label = this.view
        .text(this.attributes.label)
        .font({ family: 'Roboto', style: 'normal' })
        .addClass('label block-label')
        .front();
    }
  }

  render() {
    if (this.attributes.label) {
      this.label!.text(this.attributes.label);
    }
  }

  onDragStart() {
    this.seats.forEach((s) => s.onDragStart());
  }

  onDragMove(dx: number, dy: number) {
    this.seats.forEach((s) => s.onDragMove(dx, dy));
  }

  onDragEnd() {
    this.seats.forEach((s) => s.onDragEnd());
    this.rows.forEach((r) => {
      r.renderLabel();
    });
  }

  onRotateStart() {
    this.seats.forEach((s) => s.onRotateStart());
  }

  onRotateMove(rm: SVG.Matrix) {
    this.seats.forEach((s) => s.onRotateMove(rm));
    this.rows.forEach((r) => {
      r.renderLabel();
    });
    // this.rows.forEach((r) => {
    //   r.renderLabel();
    // });
    // this.seats.forEach((s) => {
    //   s.onRotateMove(rm);
    //   if (s.shapeCenter) {
    //     const sc = s.shapeCenter;
    //     const irm = this.getReverseRotationMatrix(angle, sc.x, sc.y);
    //     s.labelOrientation(irm);
    //   }
    // });
    // this.rows.forEach((r) => {
    //   r.renderLabel();
    // });
  }

  getReverseRotationMatrix(angle: number, cx: number, cy: number) {
    return new SVG.Matrix().rotate(-angle, cx, cy);
  }

  onRotateEnd() {
    this.seats.forEach((s) => s.onRotateEnd());
    this.rows.forEach((r) => {
      r.renderLabel();
    });
  }

  toJSON() {
    const { type, uuid, sid } = this;
    const attributes = Object.assign({}, this.attributes);
    return Block.filteredJSON({
      type,
      uuid,
      sid,
      attributes,
      rows: invokeMap(this.rows, 'toJSON'),
    });
  }

  destroyView() {
    this.view.remove();
  }

  destroy(destroyRows = true) {
    if (destroyRows) forEach(this.rows, (row) => row.destroy(true, true));
    this.rows = [];
    this.destroyView();
    // this.removeEventListeners();
    eventbus.emit('block:removed', this);
  }

  static fromJSON(params: ModelParams) {
    return Block.create(params);
  }

  static create(params: ModelParams = {}) {
    const attributes = {
      label: '',
    };
    params.attributes = Object.assign({}, attributes, params.attributes);
    const block = new Block(params);
    block.param_rows = params.rows!;
    // if (!RENDERER_MODE || block.app.layout !== LAYOUT.SECTIONS) {
    forEach(block.param_rows, (rowParams) => Row.fromJSON({ ...rowParams, block }));
    eventbus.emit('row:renderLabel');
    // }
    return block;
  }

  update() {
    const currentSection = this.app.seatmap.sections.find((section) => section.uuid === this.sid);
    const viewbox = this.svg.viewbox();

    const inViewBox = this.param_rows.some((row) => {
      return row.seats.some((seat) => {
        return Seatmap.checkIntersection(viewbox, seat.attributes.position);
      });
    });

    if (!inViewBox) {
      this.visible = false;
      this.hide();
      // this.rows.forEach((row) => row.hide());
      return;
    }

    if (!this.visible && (!currentSection || currentSection.isInside)) {
      this.visible = true;
      // if rows already populated, show rather than add them again (needs testing)
      if (this.rows.length) {
        // this.rows.forEach((row) => row.show());
        this.show();
      } else {
        this.param_rows.forEach((rowParams) => Row.fromJSON({ ...rowParams, block: this }));
        eventbus.emit('row:renderLabel');
        this.seats.forEach((seat) => this.app.seatmap.addUnit(seat));
      }
    }
  }

  flip(axis = 'x', cx: number, cy: number) {
    if (cx == null && cy == null) {
      // get bbox of block by getting positions of all seats and expand bbox by half seat width times scale
      const radius = SEATRADIUS * this.app.seatmap.scale;
      const bbox = this.seats.reduce(
        (acc, seat) => {
          const [x, y] = seat.attributes.position;

          return {
            x: Math.min(acc.x, x - radius),
            y: Math.min(acc.y, y - radius),
            x2: Math.max(acc.x2, x + radius),
            y2: Math.max(acc.y2, y + radius),
          };
        },
        { x: Infinity, y: Infinity, x2: -Infinity, y2: -Infinity }
      );

      // get center of bbox
      cx = bbox.x + (bbox.x2 - bbox.x) / 2;
      cy = bbox.y + (bbox.y2 - bbox.y) / 2;
    }

    // get flip matrix
    const m = new Matrix().flip(axis, axis === 'x' ? cx : cy);

    const flipXAttribute = axis === 'x' ? !this.attributes.flip?.[0] : this.attributes.flip?.[0];
    const flipYAttribute = axis === 'y' ? !this.attributes.flip?.[1] : this.attributes.flip?.[1];
    this.attributes.flip = [flipXAttribute, flipYAttribute];

    // apply matrix to all seats
    this.seats.forEach((seat) => {
      const [x, y] = seat.attributes.position;
      const [newX, newY] = new Point(x, y).transformO(m).toArray();
      seat.attributes.position = [newX, newY];
      seat.moveShapes(newX, newY);
    });
    this.rows.forEach((row) => {
      row.renderLabel();
    });
  }
}
